import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import TGChibi, { TGChibiVariants } from '../components/TGChibi';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const hasStartedVerification = useRef(false);

  useEffect(() => {
    // Prevent double execution
    if (hasStartedVerification.current) {
      console.log('[DEBUG] Verification already started, preventing double submission');
      return;
    }

    const token = searchParams.get('token');
    console.log('[DEBUG] Token extracted from URL:', token ? token.substring(0, 5) + '...' : 'null');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. Please request a new magic link.');
      return;
    }

    // Mark that we've started verification
    hasStartedVerification.current = true;
    console.log('[DEBUG] Starting verification process');

    const verify = async () => {
      try {
        console.log('[DEBUG] Calling verifyMagicLink with token');
        const result = await verifyMagicLink(token);
        console.log('[DEBUG] Verification result:', result);
        
        if (result.success) {
          console.log('[DEBUG] Verification successful');
          setStatus('success');
          setMessage('Successfully signed in! Redirecting to dashboard...');
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          console.log('[DEBUG] Verification failed with error:', result.error);
          setStatus('error');
          setMessage(result.error || 'Verification failed. Please try again.');
        }
      } catch (error) {
        console.error('[DEBUG] Unexpected error during verification:', error);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
      }
    };

    verify();
    
  }, [searchParams, verifyMagicLink, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/TGlogo.png" 
              alt="TypoGuard Logo" 
              className="w-12 h-12 object-contain"
            />
          </div>
          
          {status === 'verifying' && (
            <>
              <TGChibiVariants.Loading className="mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Verifying your email...
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Please wait while we sign you in.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <TGChibiVariants.Success className="mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Welcome to TypoGuard!
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {message}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <TGChibiVariants.Error className="mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Verification failed
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {message}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/login')}
                  className="bg-mint-600 text-white px-6 py-2 rounded-md hover:bg-mint-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
